import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import useSEO from "../lib/useSEO";
import useReveal from "../lib/useReveal";
import SectionHeading from "../components/SectionHeading";
import { IMG } from "../lib/media";

export default function Contact() {
  const ref = useReveal();
  useSEO({
    title: "Contact — SugaSpice Bakery & Kitchen, St Neots",
    description: "Get in touch with SugaSpice Bakery & Kitchen in St Neots. General enquiries, press, partnerships, or wholesale.",
    image: IMG.interior_wood,
  });

  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill the required fields.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact/messages", form);
      setDone(true);
      toast.success("Message received. We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div ref={ref}>
      <section className="pt-12 md:pt-16 pb-16 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="fade-up max-w-3xl">
            <span className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#C8102E]">Get in touch</span>
            <h1 className="mt-5 font-display text-[clamp(2.6rem,7vw,6rem)] leading-[0.9] tracking-tight uppercase">
              Say hello. <br /><span className="text-[#C8102E]">We're listening.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#3a3a3a]">General enquiries, press, events, partnerships — drop us a line. For wholesale, please use the wholesale enquiry form for the fastest response.</p>
            <p className="mt-4 text-base text-[#3a3a3a] leading-relaxed">
              Whether you've got a question about our menu, want to talk about a large order or celebration cake, are planning an event and need catering, or you're a local business interested in stocking SugaSpice, we'd love to hear from you. We aim to reply to every message within one working day. If your enquiry is urgent, the quickest way to reach us is by phone during opening hours, or pop into the bakery on Moores Walk in St Neots and speak to the team in person.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-4 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 fade-up">
            {[
              { t: "Orders & enquiries", d: "Questions about the menu, allergens, collection or delivery, or placing a larger order for an office, party or event." },
              { t: "Wholesale supply", d: "Forecourts, convenience stores, cafés and retailers looking to stock fresh SugaSpice bakery and grab-and-go products." },
              { t: "Press & partnerships", d: "Media requests, collaborations and community partnerships across St Neots and Cambridgeshire." },
            ].map((c) => (
              <div key={c.t} className="bg-white rounded-3xl border border-[#D1C9BE]/60 p-6">
                <h3 className="font-display text-xl uppercase tracking-tight">{c.t}</h3>
                <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 fade-up">
            <div className="bg-[#1A1A1A] text-[#F7F5F0] rounded-3xl p-8 md:p-10">
              <h3 className="font-display text-3xl uppercase tracking-tight">Direct lines</h3>
              <ul className="mt-8 space-y-6 text-sm">
                <li className="flex gap-4"><Mail className="h-5 w-5 mt-1 text-[#C8102E]" /><div><p className="font-semibold">General</p><p className="text-[#C9C5BE]">sugaspicebakery01@gmail.com</p></div></li>
                <li className="flex gap-4"><Mail className="h-5 w-5 mt-1 text-[#C8102E]" /><div><p className="font-semibold">Wholesale</p><p className="text-[#C9C5BE]">sugaspicebakery01@gmail.com</p></div></li>
                <li className="flex gap-4"><Phone className="h-5 w-5 mt-1 text-[#C8102E]" /><a href="tel:+441480763192" className="text-[#C9C5BE] hover:text-[#F7F5F0] transition-colors" data-testid="contact-phone">01480 763192</a></li>
                <li className="flex gap-4"><MapPin className="h-5 w-5 mt-1 text-[#C8102E]" /><p className="text-[#C9C5BE]">10 Moores Walk,<br />St Neots, PE19 1AG, UK</p></li>
              </ul>
              <div className="mt-10 pt-8 border-t border-white/10">
                <p className="text-[11px] tracking-[0.22em] uppercase text-[#A3A3A3]">Looking to stock SugaSpice?</p>
                <Link to="/wholesale" className="btn-pill mt-4 bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24]" data-testid="contact-wholesale-shortcut">
                  Wholesale Enquiry <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div className="bg-[#C8102E] text-[#F7F5F0] rounded-3xl p-10 fade-up" data-testid="contact-success">
                <CheckCircle2 className="h-10 w-10" />
                <h3 className="font-display text-3xl uppercase tracking-tight mt-5">Message received.</h3>
                <p className="mt-3">Thanks for reaching out — we'll come back to you shortly.</p>
                <button onClick={() => setDone(false)} className="btn-pill mt-8 bg-[#F7F5F0] text-[#1A1A1A] hover:bg-white" data-testid="contact-success-reset">Send another</button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-3xl p-7 md:p-10 border border-[#D1C9BE]/60 grid grid-cols-1 md:grid-cols-2 gap-5 fade-up" data-testid="contact-form">
                <Field label="Your name *"><input required value={form.name} onChange={upd("name")} data-testid="cf-name" /></Field>
                <Field label="Email *"><input type="email" required value={form.email} onChange={upd("email")} data-testid="cf-email" /></Field>
                <Field label="Phone"><input value={form.phone} onChange={upd("phone")} data-testid="cf-phone" /></Field>
                <Field label="Subject"><input value={form.subject} onChange={upd("subject")} data-testid="cf-subject" /></Field>
                <Field label="Message *" full><textarea rows={5} required value={form.message} onChange={upd("message")} data-testid="cf-message" /></Field>
                <p className="md:col-span-2 text-xs text-[#5C5C5C] leading-relaxed" data-testid="contact-privacy-note">
                  We'll use your information to respond to your enquiry. See our{" "}
                  <Link to="/privacy" className="text-[#C8102E] underline">Privacy Policy</Link> for details.
                </p>
                <div className="md:col-span-2 flex justify-end">
                  <button type="submit" disabled={sending} className="btn-pill bg-[#1A1A1A] text-[#F7F5F0] hover:bg-[#333] disabled:opacity-60" data-testid="cf-submit">
                    {sending ? "Sending…" : "Send Message"} <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, full = false }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-[11px] tracking-[0.18em] uppercase text-[#5C5C5C] font-semibold">{label}</span>
      <span className="[&_input]:bg-[#F7F5F0] [&_textarea]:bg-[#F7F5F0] [&_input]:border [&_textarea]:border [&_input]:border-[#D1C9BE] [&_textarea]:border-[#D1C9BE] [&_input]:rounded-2xl [&_textarea]:rounded-2xl [&_input]:px-5 [&_textarea]:px-5 [&_input]:py-3.5 [&_textarea]:py-3.5 [&_input]:text-sm [&_textarea]:text-sm [&_input]:w-full [&_textarea]:w-full focus-within:[&_input]:border-[#C8102E] focus-within:[&_textarea]:border-[#C8102E] [&_input]:outline-none [&_textarea]:outline-none">
        {children}
      </span>
    </label>
  );
}
